import { useAuth } from '../providers/auth'
import { getChatApiUrl, v1Client } from '../api'
import { useEffect, useState } from 'react'

export const useChatImage = (fileId?: string) => {
  const [imageUrl, setImageUrl] = useState<string | null>(null)
  const [isImageLoading, setIsImageLoading] = useState(false)
  const [imageError, setImageError] = useState<Error | null>(null)
  const { getAuthToken } = useAuth()

  useEffect(() => {
    let isMounted = true
    let retries = 3

    const fetchImage = async () => {
      if (!fileId) {
        setImageUrl(null)
        setIsImageLoading(false)
        setImageError(null)
        return
      }

      setIsImageLoading(true)
      setImageError(null)

      try {
        const backendHostname = (() => {
          try {
            return new URL(getChatApiUrl() ?? '').hostname
          } catch {
            return ''
          }
        })()

        const shouldSendAuthHeader = backendHostname.endsWith('highlightai.com') || backendHostname === 'localhost'

        const imageRequest = await v1Client.GET('/files/{id}', {
          params: {
            path: {
              id: fileId,
            },
          },
          headers: shouldSendAuthHeader
            ? {
                Authorization: `Bearer ${await getAuthToken()}`,
              }
            : undefined,
          parseAs: 'arrayBuffer',
        })

        if (imageRequest.error) {
          console.error('Failed to fetch image', imageRequest.error)
          throw new Error('Failed to fetch image')
        }

        const imageBuffer = imageRequest.data
        const mimeType = imageRequest.response.headers.get('content-type')

        if (!mimeType) {
          console.error('No content type found for image: ' + fileId)
          throw new Error('No content type found for image')
        }

        const blob = new Blob([imageBuffer], { type: mimeType })
        const url = URL.createObjectURL(blob)

        if (isMounted) {
          setImageUrl(url)
        }
      } catch (err) {
        if (retries > 0) {
          console.warn('Image failed to load, retrying...', err)
          retries--
          setTimeout(fetchImage, 1000)
        } else if (isMounted) {
          setImageError(err instanceof Error ? err : new Error('Failed to fetch image'))
        }
      } finally {
        if (isMounted) {
          setIsImageLoading(false)
        }
      }
    }

    fetchImage()

    return () => {
      isMounted = false
      if (imageUrl) {
        URL.revokeObjectURL(imageUrl)
      }
    }
  }, [fileId])

  return { imageUrl, isImageLoading, imageError }
}
